import { useEffect, useState, type ReactNode } from 'react';
import { api, type League, type Season, type Sponsor } from '../lib/api';
import { roundsNoun } from '../lib/round-name';

/**
 * 8Ball Padel's own homepage — the platform, not a league.
 *
 * This is the one place the full-colour lockup is the star. Inside a league it
 * steps back to a single-colour credit so it never competes with that league's
 * branding.
 *
 * Everything league-specific here is read from the API: the name, the blurb,
 * the counts, even the word a league uses for its sides. The page has no idea
 * which leagues exist, so league number three needs a migration and nothing
 * else.
 */

/** WhatsApp is how leagues actually reach us, so both calls to action open it. */
const WHATSAPP = '27728677227';

function whatsapp(message: string): string {
  return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`;
}

const START_LEAGUE = whatsapp("Hi, I'd like to get my padel league set up on 8Ball Padel");
const SPONSOR = whatsapp("Hi, I'm interested in sponsoring a league on 8Ball Padel");

const FEATURES = [
  {
    icon: '📊',
    title: 'Live Scoring',
    body: 'Input scores court-side as they happen. Standings update instantly — no more waiting days for someone to update a spreadsheet.',
  },
  {
    icon: '🎯',
    title: 'Your Rules, Built Right',
    body: 'Every league scores differently. We build your exact points system — tiebreaks, bonuses, whatever makes your league yours.',
  },
  {
    icon: '🎨',
    title: 'Fully Branded',
    body: 'Your league keeps its own name, colours, and identity. 8Ball Padel is the engine — your league is still the show.',
  },
  {
    icon: '📱',
    title: 'Installable, Anywhere',
    body: 'Players add your league straight to their home screen — live results, no app store, works even with patchy court wifi.',
  },
  {
    icon: '📸',
    title: 'Instagram Ready',
    body: 'Every match night becomes a post. A results graphic in your colours, a caption already written, and every team tagged — one tap from the app to Instagram, the same night it was played.',
  },
  {
    icon: '🔗',
    title: 'Fits Your Website',
    body: 'Already have a site? Link straight to your league, or drop the live table into a page of your own. No site yet? This is one — your league has a web home from day one.',
  },
];

const STEPS = [
  {
    title: 'Tell us your league',
    body: 'Teams, players, fixtures, and how you score matches. However organised (or not) it currently is — we’ll work with what you’ve got.',
  },
  {
    title: 'We build it',
    body: 'Your scoring system, your fixtures, your branding — set up and tested before a single ball is served.',
  },
  {
    title: 'Go live',
    body: 'Match night, scores go in live, standings update in real time. Your players see it all — on their phones, instantly.',
  },
];

export default function Platform() {
  const [leagues, setLeagues] = useState<League[] | null>(null);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .leagues()
      .then((data) => setLeagues(data.leagues))
      .catch((e: Error) => setError(e.message));
    // Sponsors are never worth surfacing an error for.
    api
      .sponsors()
      .then((data) => setSponsors(data.sponsors))
      .catch(() => setSponsors([]));
  }, []);

  return (
    <main className="pf">
      <header className="pf-hero">
        {/*
          WebP, and transparent: the lockup sits on the page's gradient rather
          than in a black box of its own, and the glow below comes from CSS so it
          follows the mark's actual silhouette. 100KB rather than 1.2MB.
        */}
        <img
          className="pf-hero-logo"
          src="/brand/8ball-padel.webp"
          width={706}
          height={720}
          alt="8Ball Padel"
        />
        <h1>Run your padel league like it’s the real thing.</h1>
        <p>
          Live scoring, real-time standings, and a professional home for your league — built to
          maximise the fun, seamlessly.
        </p>
        <div className="pf-cta-row">
          <a className="pf-btn primary" href={START_LEAGUE}>Start Your League</a>
          {/*
            Scrolls without touching the hash. The hash is the router here, so an
            ordinary in-page anchor would leave the URL claiming a route that
            does not exist.
          */}
          <a
            className="pf-btn"
            href="#leagues"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('leagues')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            See It In Action
          </a>
        </div>
      </header>

      <SectionLabel>The Problem</SectionLabel>
      <div className="pf-split">
        <div className="pf-card problem">
          <p className="pf-tag">Sound familiar?</p>
          <h3>Spreadsheets. WhatsApp arguments. Disputed ratings.</h3>
          <p>
            Most padel leagues are held together with group chats and manually-updated
            spreadsheets. Scores get typed in wrong, standings lag behind reality, and every season
            someone complains the ratings aren’t fair — because there’s no real system behind them.
          </p>
        </div>
        <div className="pf-card solution">
          <p className="pf-tag">The fix</p>
          <h3>One platform, built for team padel leagues.</h3>
          <p>
            8Ball Padel gives your league a real home — live scores as they happen, standings that
            calculate themselves, and a scoring system that’s actually documented and consistent.
            Your league, your rules, your branding — we just build the engine underneath it.
          </p>
        </div>
      </div>

      <SectionLabel>What You Get</SectionLabel>
      <div className="pf-features">
        {FEATURES.map((feature, i) => (
          <div className="pf-feature" key={feature.title}>
            <span className={`pf-feature-icon i${i + 1}`} aria-hidden="true">{feature.icon}</span>
            <h4>{feature.title}</h4>
            <p>{feature.body}</p>
          </div>
        ))}
      </div>

      <SectionLabel>How It Works</SectionLabel>
      <ol className="pf-steps">
        {STEPS.map((step, i) => (
          <li className="pf-step" key={step.title}>
            <span className="pf-step-num" aria-hidden="true">{i + 1}</span>
            <div>
              <h4>{step.title}</h4>
              <p>{step.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <SectionLabel id="leagues">Leagues On 8Ball Padel</SectionLabel>

      {error && <p className="pf-muted">Could not load leagues: {error}</p>}
      {!leagues && !error && <p className="pf-muted">Loading leagues…</p>}
      {leagues?.length === 0 && <p className="pf-muted">No leagues yet.</p>}
      {leagues?.map((league) => <LeagueCard key={league.id} league={league} />)}

      <div className="pf-badge-row">
        <span className="pf-badge">
          <span className="pf-badge-dot" aria-hidden="true" />
          Powered by 8Ball Padel
        </span>
      </div>

      {sponsors.length > 0 && (
        <>
          <SectionLabel>Our Sponsors</SectionLabel>
          <div className="pf-sponsors">
            {sponsors.map((sponsor) => (
              <SponsorTile key={sponsor.id} sponsor={sponsor} />
            ))}
          </div>
          <p className="pf-sponsor-cta">
            Interested in sponsoring a league on 8Ball Padel?{' '}
            <a href={SPONSOR}>Get in touch</a>
          </p>
        </>
      )}

      <SectionLabel>Bring Your League</SectionLabel>
      <div className="pf-final-cta">
        <h3>Ready to give your league a real home?</h3>
        <p>
          Free while we’re onboarding early leagues. Send us your teams, fixtures, and scoring
          rules — we’ll do the rest.
        </p>
        <a className="pf-btn primary" href={START_LEAGUE}>Get Started</a>
      </div>

      <footer className="pf-foot">8Ball Padel — The Home of Padel Leagues</footer>
    </main>
  );
}

function SectionLabel({ children, id }: { children: ReactNode; id?: string }) {
  return <h2 className="pf-section" id={id}>{children}</h2>;
}

/**
 * One league, linking into that league rather than back to the platform root.
 *
 * The counts come from the league's current season, so they are true on the day
 * rather than true on the day someone wrote them down.
 */
function LeagueCard({ league }: { league: League }) {
  const season = league.seasons.find((s) => !s.archived) ?? league.seasons[0];

  /*
   * The season's badge where there is one, the league's enduring mark
   * otherwise — the same choice the league's own header makes, so a card and
   * the page it opens show the same artwork.
   */
  const logo = season?.logoUrl ?? league.logoUrl;

  return (
    <a className={`pf-league theme-${league.theme}`} href={`#/l/${league.slug}`}>
      <span className="pf-league-head">
        {logo && (
          /* Decorative: the league's name is written out beside it. */
          <img className="pf-league-logo" src={logo} alt="" aria-hidden="true" loading="lazy" />
        )}
        <span className="pf-league-heading">
          <p className="pf-league-badge">
            {[league.category, league.region].filter(Boolean).join(' · ') || 'League'}
          </p>
          <h3>{league.name}</h3>
        </span>
      </span>
      {league.tagline && <p className="pf-league-blurb">{league.tagline}</p>}
      {season && (
        <p className="pf-league-stats">
          {stats(league, season).map((stat) => (
            <span key={stat.label}>
              <b>{stat.value}</b> {stat.label}
            </span>
          ))}
        </p>
      )}
      <span className="pf-league-go">View full standings →</span>
    </a>
  );
}

/**
 * The three figures worth quoting for a league.
 *
 * A season that has not started yet has no matches to boast about, so it
 * advertises its start date instead of a zero.
 */
function stats(league: League, season: Season): { value: string; label: string }[] {
  const counts = season.stats;
  if (!counts) return [];

  const third =
    counts.matchesPlayed > 0
      ? { value: String(counts.matchesPlayed), label: 'Matches Played' }
      : season.startsOn
        ? { value: 'Starts', label: shortDate(season.startsOn) }
        : { value: String(counts.gameweeks), label: roundsNoun() };

  return [
    { value: String(counts.teams), label: league.unitNoun },
    { value: String(counts.players), label: 'Players' },
    third,
  ];
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * A card each, rather than a logo in a strip.
 *
 * The name is set in type and the sponsor's own line beside it, with the mark
 * to the right. A strip says only that these businesses exist; a card gives
 * each one something a reader takes in, which is what a sponsor is buying.
 *
 * Not colour-coded per sponsor: all three of ours measure within twenty degrees
 * of hue of each other, so tinting each card by its brand would give three
 * near-identical amber blocks. Dark is also the ground every one of these marks
 * already reads on. See `.pf-sponsor` for the sizing note.
 */
function SponsorTile({ sponsor }: { sponsor: Sponsor }) {
  const body = (
    <>
      <div className="pf-sponsor-text">
        <p className="pf-sponsor-lab">Sponsor</p>
        <h4>{sponsor.name}</h4>
        {sponsor.tagline && <p className="pf-sponsor-tag">{sponsor.tagline}</p>}
      </div>
      {sponsor.logoUrl && (
        <img src={sponsor.logoUrl} alt="" loading="lazy" decoding="async" />
      )}
    </>
  );

  if (!sponsor.websiteUrl) return <div className="pf-sponsor">{body}</div>;

  return (
    <a
      className="pf-sponsor"
      href={sponsor.websiteUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
    >
      {body}
    </a>
  );
}
